import * as backend from '@/api/backend';
import { checkResponseStatus } from '@/util/check';
import { formatRallyForApi, formatRallyForEdit } from '@/util/coerce';

const defaultSegmentationConfig = {
  visibility: {
    showParticipantCount: true,
    carouselAudience: 'ALL',
  },
  access: {
    userTier: 'ALL',
    genders: null,
    ageMin: null,
    ageMax: null,
    ageBuckets: null,
  },
  genderQuotas: {
    enabled: false,
    counts: {
      Männlich: null,
      Weiblich: null,
      Divers: null,
    },
  },
  userSegmentQuotas: {
    enabled: false,
    quotaBucketOrder: [],
    counts: {},
  },
};

const defaultRally = {
  rallyID: null,
  title: '',
  deeplink: '',
  specialIDs: [],
  companyIDs: [],
  tags: [],
  rallyCategoryID: null,
  isPublic: true,
  requiresTicket: false,
  galleryEnabled: false,
  /** Last saved server value — upload UI waits until Rally save when toggling gallery on. */
  galleryEnabledOnServer: false,
  galleryRetentionMode: 'until_next_occurrence',
  galleryRetentionDays: 7,
  allowMultipleTourDealsPerVenue: false,
  blacklistHideFromUser: true,
  isDebugRally: false,
  excludedFromCommission: false,
  isHighlighted: false,
  maxParticipants: null,
  segmentationConfig: JSON.parse(JSON.stringify(defaultSegmentationConfig)),
  description: '',
  rallyImageUrl: '',
  shortDescription: '',
  scheduleType: 'ONCE',
  scheduleConfig: {
    gatheringMinutes: 15,
    dwellMinutesPerStop: 15,
    startTime: '14:00',
    dwellInputMode: 'manual',
    endTimeInputMode: 'calculated',
  },
  sendTourPush: false,
};

const RALLY_EDIT_SERVER_KEYS = [
  ...Object.keys(defaultRally),
  'rallyID',
  'participantCount',
  'rallyImageUrl',
  'stats',
  'deleted',
];

const applyFormattedRallyField = (target, key, formatted) => {
  if (formatted[key] === undefined) return;
  if ((key === 'startTime' || key === 'endTime') && formatted[key]) {
    target[key] = String(formatted[key]).slice(0, 5);
    return;
  }
  target[key] = formatted[key];
};

const rallyIdFromPayload = (rally) => Number(rally?.rallyID ?? rally?.rally_id);

const cloneDefaultRally = (template) => JSON.parse(JSON.stringify(template));

const buildRallyEditFromServer = (defaultRallyTemplate, rally) => {
  const formatted = formatRallyForEdit({ ...defaultRallyTemplate, ...rally });
  const next = {
    ...cloneDefaultRally(defaultRallyTemplate),
    ...formatted,
  };
  RALLY_EDIT_SERVER_KEYS.forEach((key) => {
    applyFormattedRallyField(next, key, formatted);
  });
  if (formatted.rallyImageUrl !== undefined) {
    next.rallyImageUrl = formatted.rallyImageUrl;
  }
  next.sendTourPush = false;
  next.galleryEnabledOnServer = Boolean(next.galleryEnabled);
  return next;
};

export default {
  state: {
    rallys: [],
    loading: false,
    error: false,
    rallyEdit: {
      ...defaultRally,
    },
    defaultRally,
  },
  mutations: {
    fetch(state) {
      state.loading = true;
    },
    errorRallys(state) {
      state.rallys = [];
      state.loading = false;
      state.error = true;
    },
    setRallys(state, rallys) {
      state.rallys = rallys;
      state.loading = false;
      state.error = false;
    },
    editRally(state, rally) {
      if (!rally) {
        state.rallyEdit = { ...state.defaultRally };
        return;
      }
      state.rallyEdit = buildRallyEditFromServer(state.defaultRally, rally);
    },
    setRallyEditFromServer(state, rally) {
      if (!rally) return;
      const prev = state.rallyEdit;
      const next = buildRallyEditFromServer(state.defaultRally, rally);
      const sameRally = rallyIdFromPayload(rally) === Number(prev.rallyID);
      if (sameRally && !next.rallyImageUrl && prev.rallyImageUrl) {
        next.rallyImageUrl = prev.rallyImageUrl;
      }
      state.rallyEdit = next;
    },
    updateRally(state, partial) {
      const merged = formatRallyForEdit({ ...state.rallyEdit, ...partial });
      Object.keys(partial).forEach((key) => {
        if (key === 'galleryEnabledOnServer') return;
        if (merged[key] !== undefined) {
          state.rallyEdit[key] = merged[key];
        } else {
          state.rallyEdit[key] = partial[key];
        }
      });
    },
    patchRallyInList(state, { rallyID, patch }) {
      const id = Number(rallyID);
      const index = state.rallys.findIndex((r) => Number(r.rallyID) === id);
      if (index === -1) return;
      const prev = state.rallys[index];
      const formatted = formatRallyForEdit({ ...prev, ...patch });
      const next = { ...prev, ...formatted, rallyID: id };
      const patchHasImageUrl = 'rallyImageUrl' in patch || 'rally_image_url' in patch;
      if (!patchHasImageUrl && prev.rallyImageUrl) {
        next.rallyImageUrl = prev.rallyImageUrl;
      }
      state.rallys[index] = next;
    },
  },
  actions: {
    async fetchRallys({ commit }) {
      commit('fetch');
      try {
        const response = await backend.getRallys();
        await checkResponseStatus(200, response);
        const json = response.data;
        commit('setRallys', json);
      } catch (e) {
        console.error(e);
        commit('errorRallys');
      }
    },
    async createRally({ commit }, { rally }) {
      const body = formatRallyForApi(rally);
      if (rally.sendTourPush) {
        body.sendTourPush = true;
      }
      const response = await backend.postRally(body);
      await checkResponseStatus(201, response);

      const { rallyID } = response.data;
      commit('updateRally', { sendTourPush: false });
      return { rallyID };
    },
    async saveRallyImage({ commit, state }, { rallyID, file }) {
      const response = await backend.postImage(`/image/rally/${rallyID}`, file);
      await checkResponseStatus([200, 201], response);
      const id = Number(rallyID);
      if (Number(state.rallyEdit.rallyID) === id) {
        const base = (import.meta.env.VITE_BACKEND_URL || '').replace(/\/$/, '');
        const rallyImageUrl = `${base}/image/rally/${id}?${Date.now()}`;
        commit('updateRally', { rallyImageUrl });
        commit('patchRallyInList', { rallyID: id, patch: { rallyImageUrl } });
      }
      return response;
    },
    async fetchRallyEdit({ commit }, rallyID) {
      const response = await backend.getRally(rallyID);
      await checkResponseStatus(200, response);
      commit('editRally', response.data);
      commit('patchRallyInList', { rallyID, patch: response.data });
      return response.data;
    },
    async saveRally({ commit }, rally) {
      const body = formatRallyForApi(rally);
      if (rally.sendTourPush) {
        body.sendTourPush = true;
      }
      const response = await backend.patchRally(rally.rallyID, body);
      await checkResponseStatus(200, response);
      if (response.data) {
        commit('setRallyEditFromServer', response.data);
        commit('patchRallyInList', { rallyID: rally.rallyID, patch: response.data });
      } else {
        commit('updateRally', { sendTourPush: false });
      }
    },
    async fetchParticipationStats(context, rallyID) {
      const response = await backend.getRallyParticipationStats(rallyID);
      await checkResponseStatus(200, response);
      return response.data;
    },
    // eslint-disable-next-line no-empty-pattern
    async deleteRally(context, { rallyID }) {
      const response = await backend.deleteRally(rallyID);
      await checkResponseStatus(204, response);
    },
  },
  namespaced: true,
};
